import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQueryWithReauth';
import { OrganizationRole } from '../organization/organizationType';
import type {
  McpTokensResponse,
  McpNewTokenResponse,
  CreateMcpTokenRequest,
  McpInfoResponse,
} from './models';

const MCP_TOKENS_TAG = 'mcp_tokens';

export const mcpTokensApi = createApi({
  reducerPath: 'mcp_tokens',
  baseQuery: baseQueryWithReauth,
  tagTypes: [MCP_TOKENS_TAG],
  endpoints: (build) => ({
    getMcpTokens: build.query<McpTokensResponse, { role: OrganizationRole }>({
      query: ({ role }) => ({
        url: `/${role}/settings/mcp-tokens`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: MCP_TOKENS_TAG, id: 'LIST' }],
    }),
    createMcpToken: build.mutation<
      McpNewTokenResponse,
      { role: OrganizationRole; data: CreateMcpTokenRequest }
    >({
      query: ({ role, data }) => ({
        url: `/${role}/settings/mcp-tokens`,
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: MCP_TOKENS_TAG, id: 'LIST' }],
    }),
    deleteMcpToken: build.mutation<void, { role: OrganizationRole; tokenId: number }>({
      query: ({ role, tokenId }) => ({
        url: `/${role}/settings/mcp-tokens/${tokenId}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      async onQueryStarted({ tokenId }, { dispatch, queryFulfilled }) {
        const patches: { undo: () => void }[] = [];
        for (const role of ['partner', 'organizer'] as const) {
          patches.push(
            dispatch(
              mcpTokensApi.util.updateQueryData('getMcpTokens', { role }, (draft) => {
                draft.data = draft.data.filter((t) => t.id !== tokenId);
              }),
            ),
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),
    getMcpInfo: build.query<McpInfoResponse, { role: OrganizationRole }>({
      query: ({ role }) => ({
        url: `/${role}/settings/mcp-info`,
        headers: { Accept: 'application/json' },
      }),
    }),
  }),
});

export const {
  useGetMcpTokensQuery,
  useCreateMcpTokenMutation,
  useDeleteMcpTokenMutation,
  useGetMcpInfoQuery,
} = mcpTokensApi;
